import { useContext, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Button,
  InputCurrency,
  InputDateTime,
  InputNumber,
  InputText,
  Label,
  Loader,
  ModalConfirm,
  Switch,
  TextArea,
  theme,
  toast
} from '@mainstem/react-mainstem'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBoxOpen,
  faCalendarAlt,
  faCheck,
  faChevronDown,
  faChevronUp,
  faClipboardCheck,
  faEye,
  faFileSignature,
  faLink,
  faLock,
  faMagicWandSparkles,
  faMapMarkerAlt,
  faPaperPlane,
  faPencil,
  faPlus,
  faRedo,
  faSearch,
  faSparkles,
  faTimes,
  faTrash,
  faTriangleExclamation
} from '@fortawesome/pro-light-svg-icons'
import { AppContext } from 'context'
import { MainStemApi } from 'api-new'
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'components/ui'
import { ModalSubmitForApproval } from 'components/Modals'
import { ModalChangeAddress } from './ModalChangeAddress'
import { IAddress, IDrawerQuickRFQ, ILineItem } from './types'

type SectionKey = 'address' | 'items' | 'dates' | 'sourcing' | 'review'

const SECTIONS: { key: SectionKey; label: string; icon: any }[] = [
  { key: 'address', label: "Where's it going?", icon: faMapMarkerAlt },
  { key: 'items', label: 'Items you need', icon: faBoxOpen },
  { key: 'dates', label: 'When do you need it?', icon: faCalendarAlt },
  { key: 'sourcing', label: 'Sourcing Options', icon: faSearch },
  { key: 'review', label: 'Review', icon: faClipboardCheck }
]

function normalizeLineItemQuantity(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return 1
  const int = Math.trunc(n)
  return int < 1 ? 1 : int
}

let lineItemCounter = 0

const DrawerQuickRFQ: React.FC<IDrawerQuickRFQ> = ({ onClose }) => {
  const history = useHistory()
  const { loggedInUser } = useContext(AppContext)

  const defaultAddress: IAddress = {
    address1: loggedInUser?.defaultLocation?.defaultAddressShipTo?.address1 || '',
    address2: loggedInUser?.defaultLocation?.defaultAddressShipTo?.address2 || '',
    city: loggedInUser?.defaultLocation?.defaultAddressShipTo?.city || '',
    state: loggedInUser?.defaultLocation?.defaultAddressShipTo?.state || '',
    zip: loggedInUser?.defaultLocation?.defaultAddressShipTo?.zip || '',
    country: loggedInUser?.defaultLocation?.defaultAddressShipTo?.country || ''
  }

  const [activeSection, setActiveSection] = useState<SectionKey>('address')
  const [completedSections, setCompletedSections] = useState<Set<SectionKey>>(new Set())

  const [address, setAddress] = useState<IAddress>(defaultAddress)
  const [showChangeAddress, setShowChangeAddress] = useState(false)

  const [lineItems, setLineItems] = useState<ILineItem[]>([
    {
      id: `li-${Date.now()}-0`,
      name: '',
      url: '',
      imageUrl: '',
      quantity: 1,
      dateRequired: null,
      openToAlternativeItems: false,
      canBeSourcedFromAnySupplier: false,
      targetPricePerUnitCustomer: ''
    }
  ])

  const [globalDate, setGlobalDate] = useState<Date | null>(null)
  const [useDifferentDates, setUseDifferentDates] = useState(false)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [bulkQuantity, setBulkQuantity] = useState<number>(1)
  const [showBulkQuantity, setShowBulkQuantity] = useState(false)
  const [bulkDate, setBulkDate] = useState<Date | null>(null)
  const [showBulkDate, setShowBulkDate] = useState(false)
  const [globalAnySupplier, setGlobalAnySupplier] = useState(false)
  const [globalOpenToAlternatives, setGlobalOpenToAlternatives] = useState(false)
  const [useDifferentSourcing, setUseDifferentSourcing] = useState(false)
  const [generatingAI, setGeneratingAI] = useState(false)
  const aiRequestedRef = useRef(false)

  const itemsSectionRef = useRef<HTMLDivElement>(null)

  const [submitting, setSubmitting] = useState(false)
  const [submittingToSend, setSubmittingToSend] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [createdRfqId, setCreatedRfqId] = useState<number | null>(null)
  const [showSubmitForApprovalModal, setShowSubmitForApprovalModal] = useState(false)
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false)

  const [scrapeModalLineItemId, setScrapeModalLineItemId] = useState<string | null>(null)
  const [scrapeUrl, setScrapeUrl] = useState('')
  const [scraping, setScraping] = useState(false)
  const [scrapeError, setScrapeError] = useState('')

  // --- helpers ---

  const sectionIndex = (key: SectionKey) => SECTIONS.findIndex((s) => s.key === key)

  const isSectionAccessible = (key: SectionKey) => {
    const idx = sectionIndex(key)
    if (idx === 0) return true
    const prev = SECTIONS[idx - 1].key
    return completedSections.has(prev)
  }

  const markComplete = (key: SectionKey) => {
    setCompletedSections((prev) => new Set(prev).add(key))
    const idx = sectionIndex(key)
    if (idx < SECTIONS.length - 1) {
      setActiveSection(SECTIONS[idx + 1].key)
    }
  }

  const toggleSection = (key: SectionKey) => {
    if (!isSectionAccessible(key)) return
    setActiveSection(activeSection === key ? (null as any) : key)
  }

  const isSectionComplete = (key: SectionKey) => completedSections.has(key)

  useEffect(() => {
    if (activeSection !== 'items') return
    requestAnimationFrame(() => {
      const input = itemsSectionRef.current?.querySelector('input[type="text"]') as HTMLInputElement | null
      input?.focus()
    })
  }, [activeSection])

  useEffect(() => {
    if (activeSection !== 'review' || aiRequestedRef.current) return
    aiRequestedRef.current = true

    setGeneratingAI(true)

    const itemsSummary = lineItems
      .map((item, i) => {
        const date = useDifferentDates
          ? item.dateRequired?.toLocaleDateString() || 'TBD'
          : globalDate?.toLocaleDateString() || 'TBD'
        return `${i + 1}. ${item.name || 'Untitled'} (Qty: ${item.quantity}, Need by: ${date})`
      })
      .join('\n')

    const addressSummary = `${address.address1}, ${address.city}, ${address.state} ${address.zip}, ${address.country}`

    const prompt = `You are helping a procurement professional create a Request for Quote (RFQ).

STRICT RULES:
- Only reference the EXACT items listed below. Do NOT invent, infer, or add any items that are not explicitly listed.
- The title should be a concise summary (max 80 chars) derived only from the item names provided.
- The description should be 2-3 professional sentences summarizing the request, referencing only the items and details below.
- If an item has an associated URL, mention it as a referenced product link rather than guessing what it is.

Ship to: ${addressSummary}

Items requested (use ONLY these — do not add or change any):
${itemsSummary}

Respond in JSON format only: {"title": "...", "description": "..."}`

    MainStemApi.api
      .aiChat({ message: prompt })
      .then((res) => {
        let content = res.data.response || ''
        content = content.replace(/^```(?:json)?\s*\n?/i, '').replace(/\n?```\s*$/i, '')
        const parsed = JSON.parse(content)
        if (parsed.title) setTitle(parsed.title)
        if (parsed.description) setDescription(parsed.description)
      })
      .catch(() => {
        // Silently fail — user can fill in manually
      })
      .finally(() => {
        setGeneratingAI(false)
      })
  }, [activeSection])

  const canCompleteAddress = address.address1.trim().length > 0 && address.city.trim().length > 0
  const hasTitle = title.trim().length > 0
  const canCompleteItems =
    lineItems.length > 0 && lineItems.every((li) => li.name.trim().length > 0 && li.quantity >= 1)
  const canCompleteDates = useDifferentDates ? lineItems.every((li) => li.dateRequired !== null) : globalDate !== null

  // --- line item handlers ---

  const handleAddLineItem = () => {
    lineItemCounter++
    setLineItems((prev) => [
      ...prev,
      {
        id: `li-${Date.now()}-${lineItemCounter}`,
        name: '',
        url: '',
        imageUrl: '',
        quantity: 1,
        dateRequired: null,
        openToAlternativeItems: false,
        canBeSourcedFromAnySupplier: false,
        targetPricePerUnitCustomer: ''
      }
    ])
  }

  const handleRemoveLineItem = (id: string) => {
    setLineItems((prev) => prev.filter((item) => item.id !== id))
  }

  const handleUpdateLineItem = (id: string, updates: Partial<ILineItem>) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item
        const next = { ...item, ...updates }
        if (updates.quantity !== undefined) {
          next.quantity = normalizeLineItemQuantity(updates.quantity)
        }
        return next
      })
    )
  }

  const createRfqWithLineItems = async (): Promise<number | null> => {
    const rfqResponse = await MainStemApi.api.rfqCreate({
      title: title.trim(),
      description: description.trim(),
      shipTo: {
        address: [address.address1, address.address2].filter(Boolean).join(', ').trim(),
        city: address.city.trim(),
        state: address.state.trim(),
        zip: address.zip.trim(),
        country: address.country.trim()
      }
    })

    if (!rfqResponse.data.wasSuccessful || !rfqResponse.data.id) {
      toast.error(rfqResponse.data.message || 'Failed to create RFQ')
      return null
    }

    const rfqId = rfqResponse.data.id

    for (const item of lineItems) {
      const dateRequired = useDifferentDates ? item.dateRequired : globalDate

      const lineItemResponse = await MainStemApi.api.rfqLineItemsCreate({
        rfqId,
        name: item.name.trim(),
        quantity: normalizeLineItemQuantity(item.quantity),
        openToAlternativeItems: item.openToAlternativeItems,
        canBeSourcedFromAnySupplier: item.canBeSourcedFromAnySupplier,
        dateMaterialsRequiredInFacility: dateRequired?.toISOString(),
        targetPricePerUnitCustomer: item.targetPricePerUnitCustomer
          ? parseFloat(item.targetPricePerUnitCustomer) || undefined
          : undefined
      })

      if (!lineItemResponse.data.wasSuccessful || !lineItemResponse.data.id) {
        toast.error(lineItemResponse.data.message || `Failed to create line item: ${item.name}`)
        continue
      }

      if (item.url.trim()) {
        await MainStemApi.api.rfqLineItemsUrlCreate({
          rfqLineItemId: lineItemResponse.data.id,
          url: item.url.trim()
        })
      }
    }

    return rfqId
  }

  const handleSubmitRFQ = async () => {
    if (submitting) return
    setSubmitting(true)

    try {
      const rfqId = await createRfqWithLineItems()
      if (!rfqId) return

      setCreatedRfqId(rfqId)
      setSuccessMessage(
        'Your RFQ has been created as a draft. Would you like to view the details or close this window?'
      )
      setShowSuccessModal(true)
      toast.success('RFQ created successfully!')
    } catch {
      toast.error('An unexpected error occurred while creating the RFQ.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCreateAndSubmit = async (targetStatus: 'PendingApproval' | 'Submitted') => {
    if (submittingToSend) return
    setSubmittingToSend(true)

    try {
      const rfqId = await createRfqWithLineItems()
      if (!rfqId) return

      const updateResponse = await MainStemApi.api.rfqUpdate({
        id: rfqId,
        title: title.trim(),
        description: description.trim() || undefined,
        shipToAddress: [address.address1, address.address2].filter(Boolean).join(', ').trim(),
        shipToCity: address.city.trim(),
        shipToState: address.state.trim(),
        shipToZip: address.zip.trim(),
        shipToCountry: address.country.trim(),
        status: targetStatus
      })

      if (!updateResponse.data.wasSuccessful) {
        toast.error(updateResponse.data.message || 'Failed to update RFQ status')
        setCreatedRfqId(rfqId)
        setSuccessMessage(
          'Your RFQ was created as a draft but the status could not be updated. You can update it from the RFQ details page.'
        )
        setShowSuccessModal(true)
        return
      }

      setShowSubmitForApprovalModal(false)
      setShowSubmitConfirmModal(false)
      setCreatedRfqId(rfqId)

      if (targetStatus === 'PendingApproval') {
        setSuccessMessage('Your RFQ has been created and submitted for approval.')
        toast.success('RFQ created and submitted for approval!')
      } else {
        setSuccessMessage('Your RFQ has been created and submitted.')
        toast.success('RFQ created and submitted!')
      }

      setShowSuccessModal(true)
    } catch {
      toast.error('An unexpected error occurred.')
    } finally {
      setSubmittingToSend(false)
    }
  }

  const handleOpenScrapeModal = (lineItemId: string) => {
    setScrapeModalLineItemId(lineItemId)
    setScrapeUrl('')
    setScrapeError('')
    setScraping(false)
  }

  const handleAiScrape = async () => {
    if (!scrapeModalLineItemId || !scrapeUrl.trim()) return
    setScraping(true)
    setScrapeError('')

    try {
      const response = await MainStemApi.api.aiScrape({ url: scrapeUrl.trim() })

      if (!response.data.wasSuccessful) {
        setScrapeError(response.data.message || 'Could not extract product data from this URL.')
        return
      }

      const { name, price, imageURL } = response.data.product || {}

      handleUpdateLineItem(scrapeModalLineItemId, {
        name: name || '',
        url: scrapeUrl.trim(),
        imageUrl: imageURL || '',
        targetPricePerUnitCustomer: price ? String(price) : ''
      })

      setScrapeModalLineItemId(null)
      toast.success('Product details imported successfully!')
    } catch {
      setScrapeError('Something went wrong. Please check the URL and try again.')
    } finally {
      setScraping(false)
    }
  }

  // --- section header ---

  const renderSectionHeader = (section: typeof SECTIONS[number], index: number) => {
    const isActive = activeSection === section.key
    const isComplete = isSectionComplete(section.key)
    const isAccessible = isSectionAccessible(section.key)

    return (
      <button
        onClick={() => toggleSection(section.key)}
        disabled={!isAccessible}
        className={`
          w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
          ${
            isActive
              ? 'bg-indigo-50'
              : isComplete
              ? 'bg-emerald-50/50 hover:bg-emerald-50'
              : isAccessible
              ? 'hover:bg-slate-50'
              : 'opacity-50 cursor-not-allowed'
          }
          ${index === 0 ? 'rounded-t-xl' : ''}
        `}
      >
        <div
          className={`
            w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0
            ${
              isComplete
                ? 'bg-emerald-500 text-white'
                : isActive
                ? 'bg-indigo-600 text-white'
                : isAccessible
                ? 'bg-slate-200 text-slate-600'
                : 'bg-slate-100 text-slate-400'
            }
          `}
        >
          {isComplete ? <FontAwesomeIcon icon={faCheck} /> : index + 1}
        </div>
        <div className='flex-1 min-w-0'>
          <span
            className={`text-sm font-semibold ${
              isComplete
                ? 'text-emerald-700'
                : isActive
                ? 'text-indigo-900'
                : isAccessible
                ? 'text-slate-700'
                : 'text-slate-400'
            }`}
          >
            {section.label}
          </span>
        </div>
        {!isAccessible ? (
          <FontAwesomeIcon icon={faLock} className='text-slate-300 text-xs' />
        ) : (
          <FontAwesomeIcon icon={isActive ? faChevronUp : faChevronDown} className='text-slate-400 text-xs' />
        )}
      </button>
    )
  }

  // --- section bodies ---

  const renderAddress = () => (
    <div className='px-4 pb-4 pt-2'>
      <div className='bg-slate-50 rounded-lg border border-slate-200 p-3'>
        <p className='text-sm text-slate-700 leading-snug'>
          {address.address1}
          {address.address2 ? `, ${address.address2}` : ''}
          <br />
          {address.city}, {address.state} {address.zip}, {address.country}
        </p>
        <button
          onClick={() => setShowChangeAddress(true)}
          className='mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors'
        >
          <FontAwesomeIcon icon={faPencil} className='text-[10px]' />
          Change This Address
        </button>
      </div>
      <div className='mt-3 flex justify-end'>
        <Button color='primary' icon={faCheck} onClick={() => markComplete('address')} disabled={!canCompleteAddress}>
          Confirm Address
        </Button>
      </div>
    </div>
  )

  const handleResetQuantities = () => {
    setLineItems((prev) => prev.map((item) => ({ ...item, quantity: 1 })))
  }

  const handleSetAllQuantities = () => {
    const qty = normalizeLineItemQuantity(bulkQuantity)
    setLineItems((prev) => prev.map((item) => ({ ...item, quantity: qty })))
    setShowBulkQuantity(false)
  }

  const renderItems = () => (
    <div ref={itemsSectionRef} className='px-4 pb-4 pt-2 space-y-3'>
      {lineItems.length > 1 && (
        <div className='flex items-center gap-3 text-xs'>
          <span className='text-slate-400 uppercase tracking-wide font-semibold'>Qty Tools</span>
          <button
            onClick={handleResetQuantities}
            className='inline-flex items-center gap-1 font-medium text-slate-500 hover:text-slate-700 transition-colors'
          >
            <FontAwesomeIcon icon={faRedo} className='text-[10px]' />
            Reset All to 1
          </button>
          <span className='text-slate-300'>|</span>
          {showBulkQuantity ? (
            <div className='inline-flex items-center gap-1.5'>
              <div className='w-16'>
                <InputNumber
                  decimalPlaces={0}
                  min={1}
                  onChange={(_s, num) => setBulkQuantity(normalizeLineItemQuantity(num))}
                  value={bulkQuantity}
                />
              </div>
              <button
                onClick={handleSetAllQuantities}
                className='font-medium text-indigo-600 hover:text-indigo-800 transition-colors whitespace-nowrap'
              >
                Apply
              </button>
              <button
                onClick={() => setShowBulkQuantity(false)}
                className='font-medium text-slate-400 hover:text-slate-600 transition-colors'
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowBulkQuantity(true)}
              className='font-medium text-indigo-600 hover:text-indigo-800 transition-colors whitespace-nowrap'
            >
              Set All Quantities
            </button>
          )}
        </div>
      )}
      <div className='flex items-center gap-2 mb-1'>
        <span className='w-5 shrink-0' />
        <span className='flex-1 text-xs font-semibold text-slate-500'>
          Product Name <span className='text-red-500'>*</span>
        </span>
        <span className='w-20 shrink-0 text-xs font-semibold text-slate-500'>
          Qty <span className='text-red-500'>*</span>
        </span>
        <span className='w-28 shrink-0 text-xs font-semibold text-slate-500'>Target $/ea</span>
        <span className='w-8 shrink-0' />
      </div>
      {lineItems.map((item, index) => (
        <div key={item.id} className='rounded-lg border border-slate-200 bg-white p-3 space-y-2'>
          <div className='flex items-start gap-2'>
            <span className='text-xs font-semibold text-slate-400 mt-2.5 w-5 text-right shrink-0'>{index + 1}.</span>
            {item.imageUrl && (
              <div className='w-12 h-12 rounded-lg border border-slate-200 overflow-hidden shrink-0 bg-white'>
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  className='w-full h-full object-contain'
                  onError={(e) => {
                    ;(e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              </div>
            )}
            <div className='flex-1 min-w-0'>
              <div className='flex items-center gap-2'>
                <div className='flex-1'>
                  <InputText
                    onChange={(val) => handleUpdateLineItem(item.id, { name: val || '' })}
                    placeholder='Product name...'
                    value={item.name}
                  />
                </div>
                <div className='w-20 shrink-0'>
                  <InputNumber
                    decimalPlaces={0}
                    min={1}
                    onChange={(_s, num) => handleUpdateLineItem(item.id, { quantity: normalizeLineItemQuantity(num) })}
                    value={item.quantity}
                  />
                </div>
                <div className='w-28 shrink-0'>
                  <InputCurrency
                    decimalPlaces={5}
                    onChange={(val) => handleUpdateLineItem(item.id, { targetPricePerUnitCustomer: val || '' })}
                    placeholder='Target $/ea'
                    value={item.targetPricePerUnitCustomer}
                  />
                </div>
                <button
                  onClick={() => handleRemoveLineItem(item.id)}
                  className='inline-flex items-center justify-center w-8 h-8 text-slate-400 rounded-lg hover:text-red-600 hover:bg-red-50 transition-colors shrink-0 mt-0.5'
                  title='Remove item'
                >
                  <FontAwesomeIcon icon={faTrash} className='text-xs' />
                </button>
              </div>
            </div>
          </div>
          <div className='flex items-center gap-2 ml-7'>
            {item.url ? (
              <div className='flex items-center gap-2 flex-1 min-w-0'>
                <FontAwesomeIcon icon={faLink} className='text-xs text-slate-400 shrink-0' />
                <a
                  href={item.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-xs text-indigo-600 hover:text-indigo-800 truncate transition-colors'
                >
                  {item.url}
                </a>
                <button
                  onClick={() => handleUpdateLineItem(item.id, { url: '', imageUrl: '' })}
                  className='text-slate-400 hover:text-red-500 transition-colors shrink-0'
                  title='Remove URL'
                >
                  <FontAwesomeIcon icon={faTimes} className='text-xs' />
                </button>
              </div>
            ) : (
              <button
                onClick={() => handleOpenScrapeModal(item.id)}
                className='inline-flex items-center gap-1.5 text-xs font-medium text-purple-600 hover:text-purple-800 transition-colors'
              >
                <FontAwesomeIcon icon={faMagicWandSparkles} className='text-[10px]' />
                Import From URL
              </button>
            )}
          </div>
        </div>
      ))}
      <button
        onClick={handleAddLineItem}
        className='inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors'
      >
        <FontAwesomeIcon icon={faPlus} className='text-xs' />
        Add Item
      </button>
      <div className='flex justify-end pt-1'>
        <Button color='primary' icon={faCheck} onClick={() => markComplete('items')} disabled={!canCompleteItems}>
          Continue
        </Button>
      </div>
    </div>
  )

  const renderDates = () => (
    <div className='px-4 pb-4 pt-2 space-y-4'>
      {!useDifferentDates ? (
        <>
          <InputDateTime
            label='Date Materials Required In Facility'
            labelRequired
            onChange={(val) => setGlobalDate(val)}
            placeholder='Select date...'
            value={globalDate}
          />
          {lineItems.length > 1 && (
            <button
              onClick={() => setUseDifferentDates(true)}
              className='text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors'
            >
              Need different dates for each line item?
            </button>
          )}
        </>
      ) : (
        <>
          <div className='flex items-center justify-between'>
            <span className='text-sm font-medium text-slate-700'>Date per line item</span>
            <button
              onClick={() => setUseDifferentDates(false)}
              className='text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors'
            >
              Use a single date instead
            </button>
          </div>
          <div className='flex items-center gap-3 text-xs'>
            <span className='text-slate-400 uppercase tracking-wide font-semibold'>Date Tools</span>
            <button
              onClick={() => setLineItems((prev) => prev.map((item) => ({ ...item, dateRequired: null })))}
              className='inline-flex items-center gap-1 font-medium text-slate-500 hover:text-slate-700 transition-colors'
            >
              <FontAwesomeIcon icon={faRedo} className='text-[10px]' />
              Reset All Dates
            </button>
            <span className='text-slate-300'>|</span>
            {showBulkDate ? (
              <div className='inline-flex items-center gap-1.5'>
                <div className='w-40'>
                  <InputDateTime onChange={(val) => setBulkDate(val)} placeholder='Pick date...' value={bulkDate} />
                </div>
                <button
                  onClick={() => {
                    if (bulkDate) {
                      setLineItems((prev) => prev.map((item) => ({ ...item, dateRequired: bulkDate })))
                    }
                    setShowBulkDate(false)
                  }}
                  className='font-medium text-indigo-600 hover:text-indigo-800 transition-colors whitespace-nowrap'
                >
                  Apply
                </button>
                <button
                  onClick={() => setShowBulkDate(false)}
                  className='font-medium text-slate-400 hover:text-slate-600 transition-colors'
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowBulkDate(true)}
                className='font-medium text-indigo-600 hover:text-indigo-800 transition-colors whitespace-nowrap'
              >
                Set All Dates
              </button>
            )}
          </div>
          <div className='space-y-2'>
            {lineItems.map((item, index) => (
              <div key={item.id} className='bg-slate-50 rounded-lg border border-slate-200 p-3'>
                <div className='flex items-center justify-between mb-2'>
                  <div className='flex items-center gap-2 min-w-0'>
                    <span className='text-xs font-bold text-slate-400 shrink-0'>{index + 1}.</span>
                    <span className='text-sm text-slate-700 truncate'>{item.name || 'Untitled'}</span>
                  </div>
                  <span className='text-xs text-slate-500 shrink-0 ml-2'>Qty: {item.quantity}</span>
                </div>
                <InputDateTime
                  onChange={(val) => handleUpdateLineItem(item.id, { dateRequired: val })}
                  placeholder='Select date...'
                  value={item.dateRequired}
                />
              </div>
            ))}
          </div>
        </>
      )}
      <div className='flex justify-end pt-1'>
        <Button color='primary' icon={faCheck} onClick={() => markComplete('dates')} disabled={!canCompleteDates}>
          Continue
        </Button>
      </div>
    </div>
  )

  const handleGlobalAnySupplierChange = () => {
    const newVal = !globalAnySupplier
    setGlobalAnySupplier(newVal)
    setLineItems((prev) => prev.map((item) => ({ ...item, canBeSourcedFromAnySupplier: newVal })))
  }

  const handleGlobalOpenToAlternativesChange = () => {
    const newVal = !globalOpenToAlternatives
    setGlobalOpenToAlternatives(newVal)
    setLineItems((prev) => prev.map((item) => ({ ...item, openToAlternativeItems: newVal })))
  }

  const renderSourcing = () => (
    <div className='px-4 pb-4 pt-2 space-y-4'>
      {!useDifferentSourcing ? (
        <>
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <Label>Can this item be sourced from any supplier?</Label>
              <Switch
                isChecked={globalAnySupplier}
                onText='Yes'
                offText='No'
                onChange={handleGlobalAnySupplierChange}
                value={globalAnySupplier}
              />
            </div>
            <div>
              <Label>Can an equivalent/alternative item be quoted?</Label>
              <Switch
                isChecked={globalOpenToAlternatives}
                onText='Yes'
                offText='No'
                onChange={handleGlobalOpenToAlternativesChange}
                value={globalOpenToAlternatives}
              />
            </div>
          </div>
          {lineItems.length > 1 && (
            <button
              onClick={() => setUseDifferentSourcing(true)}
              className='text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors'
            >
              Need different settings for each line item?
            </button>
          )}
        </>
      ) : (
        <>
          <div className='flex items-center justify-between'>
            <span className='text-sm font-medium text-slate-700'>Sourcing per line item</span>
            <button
              onClick={() => setUseDifferentSourcing(false)}
              className='text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors'
            >
              Use same settings for all
            </button>
          </div>
          <div className='space-y-2'>
            {lineItems.map((item, index) => (
              <div key={item.id} className='bg-slate-50 rounded-lg border border-slate-200 p-3'>
                <div className='flex items-center gap-2 mb-2'>
                  <span className='text-xs font-bold text-slate-400'>{index + 1}.</span>
                  <span className='text-sm text-slate-700 truncate'>{item.name || 'Untitled'}</span>
                </div>
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <Label>Can this item be sourced from any supplier?</Label>
                    <Switch
                      isChecked={item.canBeSourcedFromAnySupplier}
                      onText='Yes'
                      offText='No'
                      onChange={() =>
                        handleUpdateLineItem(item.id, {
                          canBeSourcedFromAnySupplier: !item.canBeSourcedFromAnySupplier
                        })
                      }
                      value={item.canBeSourcedFromAnySupplier}
                    />
                  </div>
                  <div>
                    <Label>Can an equivalent or alternative item be quoted?</Label>
                    <Switch
                      isChecked={item.openToAlternativeItems}
                      onText='Yes'
                      offText='No'
                      onChange={() =>
                        handleUpdateLineItem(item.id, { openToAlternativeItems: !item.openToAlternativeItems })
                      }
                      value={item.openToAlternativeItems}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <div className='flex justify-end pt-1'>
        <Button color='primary' icon={faCheck} onClick={() => markComplete('sourcing')}>
          Continue
        </Button>
      </div>
    </div>
  )

  const navigateToSection = (key: SectionKey) => {
    setActiveSection(key)
  }

  const renderReview = () => {
    if (generatingAI || !aiRequestedRef.current) {
      return (
        <div className='px-4 pb-4 pt-2'>
          <div className='flex flex-col items-center justify-center py-12 gap-3'>
            <Loader />
            <div className='text-center'>
              <p className='text-sm font-medium text-slate-700'>Making final preparations for your RFQ...</p>
              <p className='text-xs text-slate-500 mt-1'>This will only take a moment.</p>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className='px-4 pb-4 pt-2 space-y-5'>
        {/* Shipping To */}
        <div>
          <div className='flex items-center justify-between mb-2'>
            <h4 className='text-xs font-semibold text-slate-500 uppercase tracking-wide'>Shipping To</h4>
            <button
              onClick={() => navigateToSection('address')}
              className='text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors'
            >
              Edit
            </button>
          </div>
          <div className='bg-slate-50 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700'>
            {address.address1}
            {address.address2 ? `, ${address.address2}` : ''}
            <br />
            {address.city}, {address.state} {address.zip}, {address.country}
          </div>
        </div>

        {/* Line Items */}
        <div>
          <div className='flex items-center justify-between mb-2'>
            <h4 className='text-xs font-semibold text-slate-500 uppercase tracking-wide'>
              Line Items ({lineItems.length})
            </h4>
            <button
              onClick={() => navigateToSection('items')}
              className='text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors'
            >
              Edit
            </button>
          </div>
          <div className='bg-slate-50 rounded-lg border border-slate-200 divide-y divide-slate-200'>
            {lineItems.map((item, index) => {
              const date = useDifferentDates
                ? item.dateRequired?.toLocaleDateString() || '—'
                : globalDate?.toLocaleDateString() || '—'
              return (
                <div key={item.id} className='px-3 py-2'>
                  <div className='flex items-center gap-2'>
                    {item.imageUrl && (
                      <div className='w-8 h-8 rounded border border-slate-200 overflow-hidden shrink-0 bg-white'>
                        <img src={item.imageUrl} alt={item.name} className='w-full h-full object-contain' />
                      </div>
                    )}
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center justify-between'>
                        <span className='text-sm font-medium text-slate-700'>
                          {index + 1}. {item.name || 'Untitled'}
                        </span>
                        <span className='text-xs text-slate-500'>Qty: {item.quantity}</span>
                      </div>
                      <div className='flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap'>
                        <span>Need by: {date}</span>
                        {item.targetPricePerUnitCustomer && <span>Target: ${item.targetPricePerUnitCustomer}/ea</span>}
                        <span>From Any Supplier: {item.canBeSourcedFromAnySupplier ? 'Yes' : 'No'}</span>
                        <span>Alternatives: {item.openToAlternativeItems ? 'Yes' : 'No'}</span>
                      </div>
                      {item.url && (
                        <div className='flex items-center gap-1 mt-1'>
                          <FontAwesomeIcon icon={faLink} className='text-[10px] text-slate-400' />
                          <a
                            href={item.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-xs text-indigo-600 hover:text-indigo-800 truncate transition-colors'
                          >
                            {item.url}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* RFQ Details */}
        <div>
          <div className='flex items-center justify-between mb-2'>
            <h4 className='text-xs font-semibold text-slate-500 uppercase tracking-wide'>RFQ Details</h4>
          </div>
          <div className='space-y-3'>
            <InputText
              label='Title'
              labelRequired
              onChange={(val) => setTitle(val || '')}
              placeholder='e.g., Q1 Supplies Restock'
              value={title}
            />
            <TextArea
              label='Description'
              onChange={(val) => setDescription(val || '')}
              placeholder='Any additional context for this request...'
              value={description}
              textAreaStyle={{ minHeight: 70 }}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderSectionBody = (key: SectionKey) => {
    switch (key) {
      case 'address':
        return renderAddress()
      case 'items':
        return renderItems()
      case 'dates':
        return renderDates()
      case 'sourcing':
        return renderSourcing()
      case 'review':
        return renderReview()
    }
  }

  return (
    <>
      <Drawer onClose={onClose} width='lg'>
        <DrawerHeader onClose={onClose}>
          <div className='flex items-center gap-3'>
            <div className='w-8 h-8 rounded-lg bg-linear-to-br from-indigo-500 to-purple-600 flex items-center justify-center'>
              <FontAwesomeIcon icon={theme.icons.mainstem.rfq} className='text-white text-sm' />
            </div>
            Quick RFQ™
          </div>
        </DrawerHeader>

        <DrawerBody className='p-0'>
          <div className='divide-y divide-slate-200'>
            {SECTIONS.map((section, index) => (
              <div key={section.key}>
                {renderSectionHeader(section, index)}
                {activeSection === section.key && renderSectionBody(section.key)}
              </div>
            ))}
          </div>
        </DrawerBody>

        <DrawerFooter>
          {isSectionComplete('sourcing') && !generatingAI && !hasTitle && (
            <div className='mr-auto flex items-center gap-1.5'>
              <FontAwesomeIcon icon={faTriangleExclamation} className='text-amber-500 text-xs' />
              <span className='text-xs font-medium text-amber-700'>Add an RFQ title to continue.</span>
            </div>
          )}
          <Button color='danger' icon={theme.icons.cancel} onClick={onClose}>
            Cancel
          </Button>
          <Button
            color='fancy'
            icon={faPaperPlane}
            disabled={!isSectionComplete('sourcing') || !hasTitle || submitting || submittingToSend || generatingAI}
            loading={submitting}
            onClick={handleSubmitRFQ}
          >
            Save As Draft
          </Button>
          <Button
            color='primary'
            icon={loggedInUser.organization.rfqRequiresApproval ? faFileSignature : faPaperPlane}
            disabled={!isSectionComplete('sourcing') || !hasTitle || submitting || submittingToSend || generatingAI}
            loading={submittingToSend}
            onClick={() => {
              if (loggedInUser.organization.rfqRequiresApproval) {
                setShowSubmitForApprovalModal(true)
              } else {
                setShowSubmitConfirmModal(true)
              }
            }}
          >
            {loggedInUser.organization.rfqRequiresApproval ? 'Submit For Approval' : 'Submit RFQ'}
          </Button>
        </DrawerFooter>
      </Drawer>

      {showChangeAddress && (
        <ModalChangeAddress
          address={address}
          onClose={() => setShowChangeAddress(false)}
          onSave={(newAddress) => {
            setAddress(newAddress)
            setShowChangeAddress(false)
          }}
        />
      )}

      {showSuccessModal && (
        <Modal onClose={() => setShowSuccessModal(false)} size='sm'>
          <ModalHeader>RFQ Created Successfully</ModalHeader>
          <ModalBody>
            <div className='flex flex-col items-center text-center gap-3 py-2'>
              <div className='w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center'>
                <FontAwesomeIcon icon={faCheck} className='text-emerald-600 text-lg' />
              </div>
              <p className='text-sm text-slate-600'>
                {successMessage ||
                  'Your RFQ has been created. Would you like to view the details or close this window?'}
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color='secondary'
              icon={faTimes}
              onClick={() => {
                setShowSuccessModal(false)
                onClose()
              }}
            >
              Close
            </Button>
            <Button
              color='primary'
              icon={faEye}
              onClick={() => {
                setShowSuccessModal(false)
                onClose()
                if (createdRfqId) {
                  history.push(`/rfq/details/${createdRfqId}`)
                }
              }}
            >
              View RFQ
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {showSubmitForApprovalModal && (
        <ModalSubmitForApproval
          onClose={() => setShowSubmitForApprovalModal(false)}
          onConfirm={() => handleCreateAndSubmit('PendingApproval')}
          loading={submittingToSend}
        />
      )}

      {showSubmitConfirmModal && (
        <ModalConfirm
          loading={submittingToSend}
          message={`This will create and submit your RFQ with ${lineItems.length} line ${
            lineItems.length === 1 ? 'item' : 'items'
          }. The status will be set to Submitted.`}
          onClose={() => setShowSubmitConfirmModal(false)}
          onConfirmDenied={() => setShowSubmitConfirmModal(false)}
          onConfirmSuccess={() => handleCreateAndSubmit('Submitted')}
          title='Submit RFQ?'
        />
      )}

      {scrapeModalLineItemId && (
        <Modal onClose={() => !scraping && setScrapeModalLineItemId(null)} size='sm'>
          <ModalHeader>
            <div className='flex items-center gap-2'>
              <FontAwesomeIcon icon={faMagicWandSparkles} className='text-purple-600' />
              Import From URL
            </div>
          </ModalHeader>
          <ModalBody>
            {scraping ? (
              <div className='flex flex-col items-center justify-center py-10 gap-4'>
                <div className='relative w-16 h-16'>
                  <div className='absolute inset-0 rounded-full border-4 border-purple-100' />
                  <div className='absolute inset-0 rounded-full border-4 border-t-purple-600 animate-spin' />
                  <div className='absolute inset-0 flex items-center justify-center'>
                    <FontAwesomeIcon icon={faSparkles} className='text-purple-600 text-lg animate-pulse' />
                  </div>
                </div>
                <div className='text-center space-y-2'>
                  <p className='text-sm font-semibold text-slate-800'>Analyzing product page...</p>
                  <p className='text-xs text-slate-500 max-w-xs'>
                    Our AI is visiting the URL and extracting product details. This may take up to a minute or two.
                  </p>
                  <div className='inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full bg-purple-50 border border-purple-100'>
                    <FontAwesomeIcon icon={faSparkles} className='text-purple-500 text-[10px]' />
                    <span className='text-[10px] font-semibold text-purple-700 uppercase tracking-wider'>
                      Powered by AI
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className='space-y-4 py-2'>
                <p className='text-sm text-slate-600'>
                  Paste a product URL below and we'll use AI to automatically extract the product name, price, and
                  image.
                </p>
                <InputText
                  label='Product URL'
                  labelRequired
                  onChange={(val) => {
                    setScrapeUrl(val || '')
                    setScrapeError('')
                  }}
                  placeholder='https://www.example.com/product...'
                  value={scrapeUrl}
                />
                {scrapeError && (
                  <div className='rounded-lg bg-red-50 border border-red-200 px-3 py-2 space-y-2'>
                    <p className='text-xs text-red-700'>{scrapeError}</p>
                    <button
                      onClick={() => {
                        handleUpdateLineItem(scrapeModalLineItemId!, { url: scrapeUrl.trim() })
                        setScrapeModalLineItemId(null)
                        toast.success('URL assigned — you can fill in the name and price manually.')
                      }}
                      className='inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors'
                    >
                      <FontAwesomeIcon icon={faLink} className='text-[10px]' />
                      Use This URL Anyway
                    </button>
                  </div>
                )}
                <div className='flex items-center gap-1.5 px-2'>
                  <FontAwesomeIcon icon={faSparkles} className='text-purple-500 text-[10px]' />
                  <span className='text-[10px] font-medium text-purple-600 uppercase tracking-wider'>
                    Powered by AI
                  </span>
                </div>
              </div>
            )}
          </ModalBody>
          {!scraping && (
            <ModalFooter>
              <Button color='secondary' icon={faTimes} onClick={() => setScrapeModalLineItemId(null)}>
                Cancel
              </Button>
              <Button color='primary' icon={faMagicWandSparkles} onClick={handleAiScrape} disabled={!scrapeUrl.trim()}>
                Import Product
              </Button>
            </ModalFooter>
          )}
        </Modal>
      )}
    </>
  )
}

export { DrawerQuickRFQ }
